import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { selectDictionary } from 'features/dictionaries/dictionariesSelectors';

import { getOptionsGroupedByParentKey } from 'utils/getOptionsGroupedByParentKey';

import { ElasticSearchFacetCounts } from 'types/commons';
import { FormattedOption } from 'types/dictionaries';
import { TranslationKey } from 'types/i18next';

import { Dictionaries } from 'constants/enums';

import SelectCommon from '../common/SelectCommon/SelectCommon';
import { SelectValue } from '../filter.types';
import { filterOptions } from '../utils/filterOptions';
import { mapOptionsWithLabels } from '../utils/mapOptionsWithLabels';

interface SkillsetSelectProps {
  value: SelectValue;
  onChange?: (value: SelectValue) => void;
  placeholder?: string;
  disabled?: boolean;
  title?: TranslationKey;
  hasAccessToFilter?: boolean;
  facets?: ElasticSearchFacetCounts;
  className?: string;
  isShowAllTags?: boolean;
}

const SkillsetSelect: FC<SkillsetSelectProps> = ({
  value,
  onChange,
  title = 'common.label.skillset',
  hasAccessToFilter = false,
  facets,
  className,
  isShowAllTags = false,
  ...restProps
}) => {
  const { t } = useTranslation();
  const skillsetDict = useSelector(
    selectDictionary(Dictionaries.Skillset)
  ) as FormattedOption[];

  const industryDict = useSelector(
    selectDictionary(Dictionaries.Industry)
  ) as FormattedOption[];

  const skillsetDictWithFacets = useMemo(() => {
    const labeled = mapOptionsWithLabels(skillsetDict, facets);

    const normalizedValue = Array.isArray(value)
      ? (value as string[])
      : undefined;

    return filterOptions(normalizedValue, labeled, facets);
  }, [skillsetDict, facets, value]);

  const skillsetOptions = useMemo(
    () =>
      getOptionsGroupedByParentKey({
        itemsList: skillsetDictWithFacets,
        labelKey: ['label'],
        valueKey: ['value'],
        parentKey: ['parentKey'],
        parentKeyMappingDict: industryDict
      }),
    [skillsetDictWithFacets, industryDict]
  );

  const handleChange = useCallback(
    (value: SelectValue) => {
      onChange?.(value);
    },
    [onChange]
  );

  if (
    !!facets &&
    (!skillsetDictWithFacets || skillsetDictWithFacets.length === 0)
  ) {
    return null;
  }

  return (
    <SelectCommon
      title={t(title)}
      value={value}
      onChange={handleChange}
      customOptions={skillsetOptions}
      placeholder={t('common.label.selectSkillset')}
      hasAccessToFilter={hasAccessToFilter}
      className={className}
      isShowAllTags={isShowAllTags}
      {...restProps}
    />
  );
};

export default SkillsetSelect;
